import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Layers, Home, Info, Zap, GraduationCap, MapPin, DollarSign, Briefcase, FileText, Image, Settings, Users, Package, Star, Handshake } from 'lucide-react';
import PageHeader from '../../components/admin/PageHeader';
import ContentCard from '../../components/admin/ContentCard';
import LoadingState from '../../components/admin/LoadingState';
import ContentManager from '../../components/admin/ContentManager';
import MediaLibrary from '../../components/admin/MediaLibrary';
import toast from 'react-hot-toast';

const ContentDashboard: React.FC = () => {
  const { section } = useParams<{ section: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  
  useEffect(() => {
    document.title = `Gestion du Contenu | Admin`;
    setLoading(false);
  }, [section]);

  const getSectionTitle = () => {
    switch (section) {
      case 'home':
        return 'Page d\'Accueil';
      case 'about':
        return 'À Propos';
      case 'products':
        return 'Produits';
      case 'services':
        return 'Services';
      case 'training':
        return 'Formation';
      case 'stations':
        return 'Stations';
      case 'proxy-power':
        return 'Proxy Power';
      case 'invest':
        return 'Investir';
      case 'careers':
        return 'Carrières';
      case 'blog':
        return 'Blog';
      case 'testimonials':
        return 'Témoignages';
      case 'partners':
        return 'Partenaires';
      case 'media':
        return 'Médiathèque';
      default:
        return 'Contenu';
    }
  };

  const getSectionIcon = () => {
    switch (section) {
      case 'home':
        return <Home size={20} />;
      case 'about':
        return <Info size={20} />;
      case 'products':
        return <Package size={20} />;
      case 'services':
        return <Zap size={20} />;
      case 'training':
        return <GraduationCap size={20} />;
      case 'stations':
        return <MapPin size={20} />;
      case 'proxy-power':
        return <Layers size={20} />;
      case 'invest':
        return <DollarSign size={20} />;
      case 'careers':
        return <Briefcase size={20} />;
      case 'blog':
        return <FileText size={20} />;
      case 'testimonials':
        return <Star size={20} />;
      case 'partners':
        return <Handshake size={20} />;
      case 'media':
        return <Image size={20} />;
      default:
        return <Settings size={20} />;
    }
  };

  const renderContent = () => {
    if (loading) {
      return <LoadingState type="form" />;
    }

    if (section === 'media') {
      return (
        <div className="space-y-6">
          <ContentCard title="Médiathèque">
            <div className="text-center py-8">
              <Image size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Gérer vos médias</h3>
              <p className="text-gray-600 mb-6">Accédez à votre bibliothèque de médias pour gérer vos images et fichiers</p>
              <button
                onClick={() => setShowMediaLibrary(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Ouvrir la médiathèque
              </button>
            </div>
          </ContentCard>
        </div>
      );
    }

    if (section === 'home') {
      return (
        <div className="space-y-6">
          <ContentManager 
            section="Bannière Principale"
            initialContent={{
              title: "Mobilité intelligente, énergie propre.",
              subtitle: "Motos connectées, recharge rapide, gestion numérique en temps réel.",
              description: "Proxy Emobility révolutionne la mobilité en Afrique avec des motos électriques innovantes, des stations de recharge solaires et des solutions de financement flexibles."
            }}
          />
          
          <ContentManager 
            section="Nos Motos Électriques"
            initialContent={{
              title: "NOS MOTOS ÉLECTRIQUES",
              description: "Découvrez notre gamme de motos électriques conçues pour répondre à tous vos besoins de mobilité. Des solutions durables, performantes et adaptées au contexte africain.",
              items: [
                {
                  id: "1",
                  title: "Terra",
                  description: "Notre modèle phare robuste et puissant, idéal pour les professionnels et la livraison",
                  image: "https://images.pexels.com/photos/2393835/pexels-photo-2393835.jpeg"
                },
                {
                  id: "2",
                  title: "Sunu",
                  description: "Élégante et performante, parfaite pour la mobilité urbaine et les déplacements quotidiens",
                  image: "https://images.pexels.com/photos/13035553/pexels-photo-13035553.jpeg"
                },
                {
                  id: "3",
                  title: "Teranga",
                  description: "Le choix économique et fiable pour tous, accessible et facile à prendre en main",
                  image: "https://images.pexels.com/photos/2611675/pexels-photo-2611675.jpeg"
                }
              ]
            }}
          />
          
          <ContentManager 
            section="Stations de Recharge"
            initialContent={{
              title: "Nos Stations de Recharge",
              description: "Découvrez notre réseau de stations Proxy Power, conçues pour vous offrir une recharge rapide, fiable et écologique partout en Afrique.",
              image: "https://images.pexels.com/photos/9800002/pexels-photo-9800002.jpeg"
            }}
          />
          
          <ContentManager 
            section="Nos Services"
            initialContent={{
              title: "Nos Services",
              description: "Des solutions complètes pour une mobilité électrique durable et accessible. Découvrez comment nous pouvons vous accompagner dans votre transition vers l'électrique.",
              items: [
                {
                  id: "1",
                  title: "Stations de Recharge",
                  description: "Réseau de stations Proxy Power pour une recharge rapide et fiable de votre véhicule électrique."
                },
                {
                  id: "2",
                  title: "Maintenance & SAV",
                  description: "Service après-vente professionnel et maintenance préventive pour garder votre moto en parfait état."
                },
                {
                  id: "3",
                  title: "Leasing & Financement",
                  description: "Solutions de financement flexibles adaptées à vos besoins pour acquérir votre moto électrique."
                },
                {
                  id: "4",
                  title: "Proxy Academy",
                  description: "Formations professionnelles pour la conduite et la maintenance des motos électriques."
                }
              ]
            }}
          />
          
          <ContentManager 
            section="BaaS System Architecture"
            initialContent={{
              title: "BaaS System Architecture",
              description: "Notre architecture Battery-as-a-Service (BaaS) intègre trois applications interconnectées pour une gestion optimale de la mobilité électrique.",
              items: [
                {
                  id: "1",
                  title: "User App",
                  description: "Application mobile intuitive pour les utilisateurs finaux",
                  image: "https://images.pexels.com/photos/6633920/pexels-photo-6633920.jpeg"
                },
                {
                  id: "2",
                  title: "Management System",
                  description: "Plateforme complète de gestion et supervision",
                  image: "https://images.pexels.com/photos/7988079/pexels-photo-7988079.jpeg"
                },
                {
                  id: "3",
                  title: "Merchant App",
                  description: "Application dédiée aux partenaires et opérateurs",
                  image: "https://images.pexels.com/photos/6633926/pexels-photo-6633926.jpeg"
                }
              ]
            }}
          />
        </div>
      );
    }

    if (section === 'about') {
      return (
        <div className="space-y-6">
          <ContentManager 
            section="Notre Mission"
            initialContent={{
              title: "Notre Mission",
              description: "Révolutionner la mobilité en Afrique grâce à des solutions électriques innovantes, durables et accessibles. Nous construisons l'avenir de la mobilité urbaine."
            }}
          />
          
          <ContentManager 
            section="Notre Vision"
            initialContent={{
              title: "Notre Vision",
              description: "Chez Proxy Emobility, nous imaginons un futur où la mobilité électrique est la norme en Afrique. Notre vision s'articule autour de trois piliers fondamentaux : Innovation, Accessibilité et Durabilité."
            }}
          />
          
          <ContentManager 
            section="Notre Équipe"
            initialContent={{
              title: "Notre Équipe",
              description: "Des experts passionnés qui dirigent notre mission de transformation de la mobilité.",
              items: [
                {
                  id: "1",
                  title: "Amadou Diallo",
                  description: "CEO & Fondateur",
                  image: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg"
                },
                {
                  id: "2",
                  title: "Marie Koné",
                  description: "Directrice Technique",
                  image: "https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg"
                },
                {
                  id: "3",
                  title: "Omar Sow",
                  description: "Directeur Commercial",
                  image: "https://images.pexels.com/photos/2379005/pexels-photo-2379005.jpeg"
                }
              ]
            }}
          />
          
          <ContentManager 
            section="Nos Bureaux"
            initialContent={{
              title: "Nos Bureaux",
              description: "Une présence stratégique pour mieux servir nos clients à travers l'Afrique.",
              items: [
                {
                  id: "1",
                  title: "Dakar, Sénégal",
                  description: "123 Avenue de l'Indépendance"
                },
                {
                  id: "2",
                  title: "Abidjan, Côte d'Ivoire",
                  description: "45 Boulevard de la République"
                },
                {
                  id: "3",
                  title: "Casablanca, Maroc",
                  description: "78 Avenue Mohammed V"
                }
              ]
            }}
          />
        </div>
      );
    }

    // Default content for other sections
    return (
      <div className="space-y-6">
        <ContentCard title={`Contenu de la section ${getSectionTitle()}`}>
          <div className="text-center py-8">
            {getSectionIcon()}
            <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">Gérer le contenu de {getSectionTitle()}</h3>
            <p className="text-gray-600 mb-6">Utilisez l'éditeur de page pour modifier le contenu de cette section</p>
            <button
              onClick={() => navigate(`/admin/pages/${section}`)}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              Éditer la page
            </button>
          </div>
        </ContentCard>
      </div>
    );
  };

  return (
    <div className="px-6">
      <PageHeader
        title={`Gestion du Contenu: ${getSectionTitle()}`}
        description="Modifiez le contenu de votre site web"
        backLink="/admin/dashboard"
      />
      
      {renderContent()}
      
      <MediaLibrary
        isOpen={showMediaLibrary}
        onClose={() => setShowMediaLibrary(false)}
        onSelect={(url) => {
          toast.success('Image sélectionnée');
          setShowMediaLibrary(false);
        }}
      />
    </div>
  );
};

export default ContentDashboard;